import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SPACING_CLASSES } from '../../styles/tokens';

/**
 * 학습 분석 기준 통일된 페이지 컨테이너 컴포넌트
 * 모바일과 데스크톱에서 일관된 여백을 제공
 */
export const PageContainer = ({ 
  children, 
  title, 
  subtitle,
  action,
  className = '',
  fluid = false,
  noPadding = false,
  spacing = 'default' // 'sm', 'default', 'lg' - 학습 분석 기준
}) => {
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 학습 분석 기준 여백 클래스
  const getSpacingClass = () => {
    if (noPadding) return '';
    
    // 모바일/데스크톱 구분 여백 적용
    const spacingMap = {
      sm: isMobileView ? 'p-3' : 'p-3',     // 12px
      default: isMobileView ? 'p-4' : 'p-4', // 16px (학습 분석 기준)
      lg: isMobileView ? 'p-4' : 'p-6'      // mobile: 16px, desktop: 24px
    };
    
    return spacingMap[spacing] || spacingMap.default;
  };

  return (
    <div className={`${fluid ? '' : 'container mx-auto'} ${getSpacingClass()} ${className}`}>
      {/* 페이지 헤더 */}
      {(title || action) && (
        <div className="section-title flex items-center justify-between">
          <div>
            {title && (
              <h1 className={`font-medium text-gray-700 ${isMobileView ? 'text-xl' : 'text-3xl'}`}>
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
            )}
          </div>
          {action && (
            <div className="flex gap-2">
              {action}
            </div>
          )}
        </div>
      )}
      
      {/* 페이지 콘텐츠 */}
      {children}
    </div>
  );
};

/**
 * 모바일 최적화 페이지 컨테이너
 * 학습 분석과 동일한 모바일 레이아웃 제공
 */
export const MobilePageContainer = ({ 
  children, 
  className = '',
  fullHeight = true,
  safeArea = true
}) => {
  const containerStyle = {
    padding: 'var(--spacing-page-mobile)',
    ...(fullHeight && {
      height: 'calc(100vh - 50px - 68px)', // 헤더 + 네비게이션
      maxHeight: 'calc(100vh - 50px - 68px)',
      overflow: 'hidden'
    }),
    ...(safeArea && {
      paddingTop: 'max(var(--spacing-page-mobile), env(safe-area-inset-top, 0px))',
      paddingBottom: 'max(var(--spacing-page-mobile), env(safe-area-inset-bottom, 0px))'
    })
  };

  return (
    <div 
      className={`flex flex-col ${className}`}
      style={containerStyle}
    >
      {children}
    </div>
  );
};

/**
 * 반응형 카드 컨테이너
 * 학습 분석 기준 카드 여백 적용
 */
export const CardContainer = ({ 
  children, 
  className = '',
  padding = 'default' // 'xs', 'sm', 'default', 'lg', 'xl'
}) => {
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 학습 분석 기준 카드 여백
  const getPaddingClass = () => {
    const paddingMap = {
      xs: SPACING_CLASSES.container.xs,
      sm: isMobileView ? SPACING_CLASSES.container.sm : SPACING_CLASSES.container.sm,
      default: isMobileView ? SPACING_CLASSES.container.sm : SPACING_CLASSES.container.default,
      lg: SPACING_CLASSES.container.lg,
      xl: SPACING_CLASSES.container.xl
    };
    
    return paddingMap[padding] || paddingMap.default;
  };

  return (
    <div className={`rounded-3xl bg-white shadow-lg ${getPaddingClass()} ${className}`}>
      {children}
    </div>
  );
};

PageContainer.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  action: PropTypes.node,
  className: PropTypes.string,
  fluid: PropTypes.bool,
  noPadding: PropTypes.bool
}; 